import type { AlchemyView } from '@wyn/contracts';
import { useEffect, useState } from 'react';
import type { TurnGameProps } from './registry.tsx';

/**
 * The Herbarium: build the cauldron here, then brew it in one move. The fire bar is display only;
 * the server times the brew itself.
 */
export function AlchemyGame({ view, busy, onMove }: TurnGameProps<AlchemyView>) {
  const [pot, setPot] = useState<number[]>([]);
  const [opened] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);
  const left = Math.max(0, view.timeLimitMs - (now - opened));
  const brewed = view.brewed !== null;
  const sum = (k: 'p' | 't' | 'v') => pot.reduce((n, i) => n + view.offer[i]![k], 0);
  const p = sum('p'), t = sum('t'), v = sum('v');
  // Each unjudged reagent may be a point either side of its usual potency, as many times as it goes in.
  const doubt = pot.filter((i) => !view.offer[i]!.known).length;
  const judging = view.offer.some((r) => !r.known);
  const add = (i: number) => {
    if (pot.length >= view.maxReagents || pot.filter((j) => j === i).length >= view.maxEach) return;
    setPot([...pot, i]);
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-ink-dim">
        Hit potency <b>exactly {view.potency}</b>, keep toxicity at <b>{view.toxicity} or less</b> and volatility at <b>{view.volatility} or less</b>, or the cauldron erupts. Up to {view.maxReagents} reagents, no more than {view.maxEach} of any one. Brew before the fire dies.
      </p>
      {judging && (
        <p className="text-sm text-ink-dim">
          Herbs vary from batch to batch. A reagent marked <b>?</b> is one you can't judge yet: its potency may be a point either side of its usual. Practice beyond this recipe's level teaches you to judge more of them.
        </p>
      )}
      <div className="flex flex-wrap justify-between gap-2 font-mono text-sm">
        <span>
          Potency <b className={p === view.potency && !doubt ? 'text-verdigris' : ''}>{doubt ? `${Math.max(0, p - doubt)}–${p + doubt}` : p}</b> / {view.potency}
        </span>
        <span>Toxicity <b className={t <= view.toxicity ? 'text-verdigris' : 'text-blood'}>{t}</b> ≤ {view.toxicity}</span>
        <span>Volatility <b className={v <= view.volatility ? 'text-verdigris' : 'text-blood'}>{v}</b> ≤ {view.volatility}</span>
      </div>
      <p className="min-h-10 border-l-2 border-rule pl-3 text-sm" aria-label="Cauldron">
        {pot.length ? pot.map((i) => view.offer[i]!.name).join(' + ') : 'The cauldron is empty.'}
      </p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {view.offer.map((r, i) => (
          <button
            key={r.name}
            type="button"
            aria-label={r.name}
            disabled={busy || brewed || pot.length >= view.maxReagents || pot.filter((j) => j === i).length >= view.maxEach}
            onClick={() => add(i)}
            className="border border-rule px-2 py-2 text-center hover:border-gilt disabled:opacity-45 disabled:hover:border-rule"
          >
            <div>{r.name}</div>
            <div className="font-mono text-xs text-ink-faint">P{r.p}{r.known ? '' : '?'} T{r.t} V{r.v}</div>
          </button>
        ))}
      </div>
      <div className="space-y-1">
        <div className="h-1.5 bg-rule" role="progressbar" aria-label="The fire" aria-valuenow={Math.round(left / 1000)}>
          <div className={`h-full ${left > 0 ? 'bg-ochre' : 'bg-blood'}`} style={{ width: `${(left / view.timeLimitMs) * 100}%` }} />
        </div>
        <div className="text-xs text-ink-faint">{left > 0 ? `The fire burns: ${Math.ceil(left / 1000)}s` : 'The fire has died; you will brew in haste.'}</div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" className="btn" disabled={busy || brewed || pot.length === 0} onClick={() => setPot(pot.slice(0, -1))}>Undo</button>
        <button type="button" className="btn" disabled={busy || brewed} onClick={() => onMove({ type: 'brew', reagents: pot })}>⚗ Brew</button>
      </div>
    </div>
  );
}
